import * as turf from '@turf/turf';
import * as geojson from 'geojson';

import * as C from 'app/utils/constants';
import * as conversionUtils from 'app/utils/conversionUtils';

// Target each split piece at 95% of MAX_AREA_ACRES so geodesic-area rounding
// and backend-vs-frontend area discrepancies can't push a piece marginally
// above MAX and re-trip the validator on the next convertFile round-trip.
export const SPLIT_SAFETY_MARGIN = 0.95;
const BISECTION_MAX_ITERATIONS = 80;
const BISECTION_PRECISION_DEGREES = 1e-6;

type Ring = number[][];

function ringArea(ring: Ring): number {
  if (ring.length < 3) return 0;
  const last = ring[ring.length - 1];
  const closed: number[][] =
    ring[0][0] === last[0] && ring[0][1] === last[1] ? ring : [...ring, ring[0]];
  return turf.area({type: 'Polygon', coordinates: [closed]});
}

/**
 * Strip the property keys the backend stamps onto features during validation
 * (__UPSTREAM_PROBLEMS, __UPSTREAM_WARNINGS, _UPSTREAM_ACRES). Used when we
 * round-trip features through convertFile a second time — the backend appends
 * to these arrays rather than overwriting them, so leaving them in would
 * duplicate every issue on each split.
 */
export function stripBackendValidationProps(
  props: geojson.GeoJsonProperties
): geojson.GeoJsonProperties {
  if (!props) return props;
  const next = {...props};
  delete next['__UPSTREAM_PROBLEMS'];
  delete next['__UPSTREAM_WARNINGS'];
  delete next['_UPSTREAM_ACRES'];
  return next;
}

/**
 * True if the feature's (Polygon or MultiPolygon) area exceeds Lens's
 * MAX_AREA_ACRES. Anything that isn't a polygonal geometry returns false.
 * Same area metric (geodesic) as the splitter uses internally.
 */
export function isFeatureOversized(feature: geojson.Feature): boolean {
  if (feature.geometry.type !== 'Polygon' && feature.geometry.type !== 'MultiPolygon') {
    return false;
  }
  const acres = conversionUtils.convert(turf.area(feature), C.UNIT_AREA_M2, C.UNIT_AREA_ACRE);
  return acres > C.MAX_AREA_ACRES;
}

function openRing(coords: number[][]): Ring {
  if (coords.length < 2) return coords.slice();
  const first = coords[0];
  const last = coords[coords.length - 1];
  if (first[0] === last[0] && first[1] === last[1]) {
    return coords.slice(0, -1);
  }
  return coords.slice();
}

function closeRing(ring: Ring): number[][] {
  if (ring.length === 0) return ring;
  return [...ring, ring[0]];
}

function ringBounds(ring: Ring): [number, number, number, number] {
  let minx = Infinity,
    miny = Infinity,
    maxx = -Infinity,
    maxy = -Infinity;
  for (const [x, y] of ring) {
    if (x < minx) minx = x;
    if (y < miny) miny = y;
    if (x > maxx) maxx = x;
    if (y > maxy) maxy = y;
  }
  return [minx, miny, maxx, maxy];
}

function ringCentroidLat(ring: Ring): number {
  let sum = 0;
  for (const pt of ring) sum += pt[1];
  return sum / ring.length;
}

// Sutherland-Hodgman clip: returns the portion of `ring` where n·p ≥ c.
// Walks consecutive edges, emitting kept endpoints and edge-crossings where
// the cut line (defined by n·p = c) intersects the ring.
function clipByHalfPlane(ring: Ring, nx: number, ny: number, c: number): Ring {
  if (ring.length === 0) return [];
  const out: Ring = [];
  let s = ring[ring.length - 1];
  let sIn = nx * s[0] + ny * s[1] >= c;
  for (let i = 0; i < ring.length; i++) {
    const e = ring[i];
    const eIn = nx * e[0] + ny * e[1] >= c;
    if (eIn) {
      if (!sIn) {
        const t = (c - (nx * s[0] + ny * s[1])) / (nx * (e[0] - s[0]) + ny * (e[1] - s[1]));
        out.push([s[0] + t * (e[0] - s[0]), s[1] + t * (e[1] - s[1])]);
      }
      out.push(e);
    } else if (sIn) {
      const t = (c - (nx * s[0] + ny * s[1])) / (nx * (e[0] - s[0]) + ny * (e[1] - s[1]));
      out.push([s[0] + t * (e[0] - s[0]), s[1] + t * (e[1] - s[1])]);
    }
    s = e;
    sIn = eIn;
  }
  return out;
}

// Binary search for the offset `c` along the normal (nx, ny) such that the
// half-plane n·p ≥ c contains `ratio` of `ring`'s total area. Initial bracket
// is the range of n·p over `ring`'s vertices; converges when the bracket is
// narrower than BISECTION_PRECISION_DEGREES.
function findBisectionOffset(
  ring: Ring,
  totalArea: number,
  ratio: number,
  nx: number,
  ny: number
): number {
  const target = totalArea * ratio;
  let cMin = Infinity;
  let cMax = -Infinity;
  for (const [x, y] of ring) {
    const c = nx * x + ny * y;
    if (c < cMin) cMin = c;
    if (c > cMax) cMax = c;
  }
  let lo = cMin;
  let hi = cMax;
  for (let i = 0; i < BISECTION_MAX_ITERATIONS; i++) {
    if (hi - lo < BISECTION_PRECISION_DEGREES) break;
    const mid = (lo + hi) * 0.5;
    if (ringArea(clipByHalfPlane(ring, nx, ny, mid)) > target) lo = mid;
    else hi = mid;
  }
  return (lo + hi) * 0.5;
}

// Choose the cut-line normal aligned to the longer of the ring's bbox axes
// (longitude span cos(lat)-corrected so degree-extents compare apples-to-
// apples at any latitude). Returns (1, 0) for an east-west cut, (0, 1) for
// north-south.
function pickCutDirection(ring: Ring): [number, number] {
  const [minx, miny, maxx, maxy] = ringBounds(ring);
  const latRad = (ringCentroidLat(ring) * Math.PI) / 180;
  const widthCorrected = (maxx - minx) * Math.cos(latRad);
  const heightDeg = maxy - miny;
  return widthCorrected >= heightDeg ? [1, 0] : [0, 1];
}

// Split `ring` into `n` connected pieces of approximately equal area by
// recursively cutting along the longer axis at the area-balancing line. Each
// leaf is a simple polygon (the half-plane clip preserves that). If a clip
// produces a degenerate result (<3 points — possible near collinear inputs),
// that branch is dropped and the output may have fewer than `n` pieces.
function recursiveBisect(ring: Ring, n: number): Ring[] {
  if (n <= 1 || ring.length < 3) return [ring];
  const k = Math.floor(n / 2);
  const [nx, ny] = pickCutDirection(ring);
  const c = findBisectionOffset(ring, ringArea(ring), k / n, nx, ny);
  const right = clipByHalfPlane(ring, nx, ny, c);
  const left = clipByHalfPlane(ring, -nx, -ny, -c);
  const out: Ring[] = [];
  if (right.length >= 3) out.push(...recursiveBisect(right, k));
  if (left.length >= 3) out.push(...recursiveBisect(left, n - k));
  return out;
}

export function splitOversizedFeature(
  feature: geojson.Feature,
  maxAreaAcres: number
): geojson.Feature[] {
  const geom = feature.geometry;
  if (geom.type !== 'Polygon' && geom.type !== 'MultiPolygon') return [feature];

  const parts: Ring[][] =
    geom.type === 'Polygon'
      ? [geom.coordinates.map(openRing)]
      : geom.coordinates.map((poly) => poly.map(openRing));

  const toAcres = (m2: number) => conversionUtils.convert(m2, C.UNIT_AREA_M2, C.UNIT_AREA_ACRE);
  const partAreasAcres = parts.map((p) => toAcres(ringArea(p[0])));
  if (partAreasAcres.every((a) => a <= maxAreaAcres)) return [feature];

  const outputRings: Ring[] = [];

  for (let pi = 0; pi < parts.length; pi++) {
    const outerRing = parts[pi][0];
    const partAcres = partAreasAcres[pi];
    if (partAcres <= maxAreaAcres) {
      outputRings.push(outerRing);
      continue;
    }
    const n = Math.max(2, Math.ceil(partAcres / (maxAreaAcres * SPLIT_SAFETY_MARGIN)));
    for (const piece of recursiveBisect(outerRing, n)) {
      if (piece.length >= 3) outputRings.push(piece);
    }
  }

  if (outputRings.length === 0) return [feature];

  const baseProps = stripBackendValidationProps(feature.properties);

  return outputRings.map((ring) => ({
    type: 'Feature',
    geometry: {type: 'Polygon', coordinates: [closeRing(ring)]},
    properties: {...(baseProps ?? {})},
  }));
}
